import { canReadDomain, canWriteProperties } from '@/api/core/authorization';
import {
  readEzremsTransitionCommand,
  type EzremsTransitionStatus,
} from '@/api/core/ezrems-transition-command';
import type {
  NativeCatalogRoom,
  NativeCatalogUnit,
  NativeTypeDetails,
} from '@/api/core/native-property-read';
import {
  readNativeProviderState,
  type NativeProviderOperation,
  type NativeProviderRoom,
} from '@/api/core/native-provider-state';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { DirtyGuard } from '@/components/dirty-guard';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { EzremsRoomBindingSection } from './ezrems-room-binding-section';
import { EzremsUnitBindingSection } from './ezrems-unit-binding-section';
import {
  EzremsUnitTransitionSection,
  pendingUnitTransitions,
} from './ezrems-unit-transition-section';
import { actor } from './presentation/command-owner';
import type { PropertyRuntime } from './property-queries';
import { UnitFirstDateSection, pendingUnitDateRequests } from './unit-first-date-section';
import { UnitPricingSection, pendingUnitPriceRequests } from './unit-pricing-section';

const EMPTY = '등록된 정보 없음';
/** 서버에 값 자체가 저장되지 않은 경우(null) — 빈 목록과 구분한다. */
const UNSET = '미설정';

/** 선택한 대상의 기본 자산 정보. 조회된 값만 보여 주고 없는 값은 없다고 적는다. */
export function AssetFacts({
  propertyName,
  type,
  room,
  unit,
  typeRoomCount,
  catalogComplete,
}: {
  propertyName: string;
  type: NativeTypeDetails | undefined;
  room: NativeCatalogRoom | undefined;
  unit: NativeCatalogUnit | undefined;
  typeRoomCount: number;
  catalogComplete: boolean;
}) {
  const level = unit ? '슬롯' : room ? '객실' : type ? '객실 유형' : '지점';
  const rows: [string, string][] = [['대상 단계', level]];
  if (unit && room) {
    rows.push(['슬롯', unit.name], ['소속 객실', room.name], ['수용 인원 단위', '1']);
  } else if (room) {
    rows.push(
      ['객실', room.name],
      ['객실 유형', type?.name ?? '확인 불가'],
      ['층', room.floor === null ? '등록된 정보 없음' : String(room.floor)],
      [
        '관리 대상 선정',
        room.selected === null ? '선정 정보 없음' : room.selected ? '선정됨' : '선정되지 않음',
      ],
      ['슬롯', `${String(room.units.length)}개`],
    );
  } else if (type) {
    rows.push(
      ['객실 유형', type.name],
      [catalogComplete ? '객실' : '불러온 객실', `${String(typeRoomCount)}개`],
    );
  } else {
    rows.push(['지점', propertyName]);
  }
  return (
    <section aria-label="선택한 대상의 기본 정보" className="rounded-md border p-4">
      <h3 className="font-semibold">선택한 대상의 기본 정보</h3>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        {rows.map(([name, value]) => (
          <div key={name}>
            <dt className="text-muted-foreground">{name}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {unit && (
        <p className="body-sm text-muted-foreground mt-3">
          슬롯 가격은 직접 설정했는지, 위 단계 값을 따르는지 가격·판매에서 확인합니다.
        </p>
      )}
    </section>
  );
}

/**
 * 객실 유형에 저장된 시설·가구·문구. 객실이나 슬롯을 고른 경우에는
 * 상위 값으로만 보여 주고, 실제 고객 화면에 어떻게 적용되는지는 단정하지 않는다.
 */
export function TypeReferenceValues({
  type,
  level,
}: {
  type: NativeTypeDetails;
  level: 'type' | 'room' | 'unit';
}) {
  const texts = [...(type.description?.texts ?? [])]
    .filter((item) => item.value?.trim())
    .sort((a, b) => (a.source_order ?? Infinity) - (b.source_order ?? Infinity));
  const furniture = type.specifications?.furniture;
  const facilities = type.specifications?.facilities;
  return (
    <section
      aria-label={level === 'type' ? '객실 유형 시설·설명' : '상위 객실 유형 값'}
      className="rounded-md border p-4"
    >
      <h3 className="font-semibold">
        {level === 'type' ? `${type.name} 시설·설명` : `상위 객실 유형(${type.name})에 저장된 값`}
      </h3>
      {level !== 'type' && (
        <p className="body-sm text-muted-foreground mt-2">
          이 {level === 'unit' ? '슬롯' : '객실'}에 직접 입력한 값은 위 문구 영역에서 확인합니다.
          아래는 상위 객실 유형에 저장된 값이며, 이 {level === 'unit' ? '슬롯' : '객실'}에 그대로
          적용되는지는 확인되지 않았습니다.
        </p>
      )}
      <div className="body-sm mt-3 space-y-3">
        <div>
          <h4 className="font-medium">표시 문구와 설명</h4>
          {type.description === null ? (
            <p className="text-muted-foreground">{UNSET}</p>
          ) : texts.length ? (
            <ul className="mt-1 space-y-1">
              {texts.map((item, index) => (
                <li key={`${item.purpose}-${item.language}-${String(index)}`}>
                  <span className="text-muted-foreground">
                    {item.purpose === 'DISPLAY_NAME' ? '표시명' : '설명'} · {item.language}:
                  </span>{' '}
                  {item.value}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">{EMPTY}</p>
          )}
        </div>
        <div>
          <h4 className="font-medium">가구</h4>
          <p>
            {type.specifications === null
              ? UNSET
              : furniture?.length
                ? furniture.join(' · ')
                : EMPTY}
          </p>
        </div>
        <div>
          <h4 className="font-medium">시설</h4>
          <p>
            {type.specifications === null
              ? UNSET
              : facilities?.length
                ? facilities.join(' · ')
                : EMPTY}
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * 슬롯(Unit) 단위 이지렘스 연결 진입. 사용자가 슬롯을 고른 뒤에만 읽는다 —
 * 슬롯 연결 API는 아직 배포되지 않은 서버 후보이므로 화면을 열 때 자동 호출하지 않는다.
 */
function UnitBindingEntry({
  runtime,
  generation,
  propertyId,
  typeId,
  room,
  selectedUnitId,
  onSelectUnit,
  onBindingChanged,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  typeId: string;
  room: NativeCatalogRoom;
  selectedUnitId?: string | null;
  onSelectUnit?: (unitId: string | null) => void;
  onBindingChanged: () => void;
}) {
  // 주소의 slot 값이 있으면 그것이 선택이다(뒤로 가기·새로고침·링크가 같은 슬롯을 연다). 없으면 화면 안 상태로 쓴다.
  const [localUnitId, setLocalUnitId] = useState<string | null>(null);
  const openUnitId = onSelectUnit ? (selectedUnitId ?? null) : localUnitId;
  const setOpenUnitId = onSelectUnit ?? setLocalUnitId;
  // 보존된 슬롯 전환 요청이 생기거나 사라지면 안내를 다시 계산한다.
  const [, setHeldTick] = useState(0);
  // 슬롯 패널의 저장하지 않은 입력. 슬롯을 바꾸거나 닫거나 탭·뒤로 가기로 떠나면 확인을 받는다(결과 불명 요청은 별도로 보존된다).
  const [dirtyPanels, setDirtyPanels] = useState<Record<string, boolean>>({});
  const dirty = Object.values(dirtyPanels).some(Boolean);
  const track = (panel: string) => (value: boolean) => {
    setDirtyPanels((current) =>
      current[panel] === value ? current : { ...current, [panel]: value },
    );
  };
  const unit = room.units.find((item) => item.id === openUnitId);
  const transitions = pendingUnitTransitions(runtime, generation);
  const prices = pendingUnitPriceRequests(runtime, generation);
  const dates = pendingUnitDateRequests(runtime, generation);
  // 세 종류의 보존 요청을 한 안내로 모은다(이름은 같은 세션에서만 보인다).
  const pendingTransitions = {
    count: transitions.count + prices.count + dates.count,
    items: [
      ...transitions.items.map((item) => ({ ...item, kind: '판매 전환' })),
      ...prices.items.map((item) => ({ ...item, kind: '가격 변경' })),
      ...dates.items.map((item) => ({ ...item, kind: '첫 입주일 변경' })),
    ],
  };
  return (
    <section aria-label="슬롯 단위 이지렘스 연결" className="mt-4 border-t pt-4">
      <DirtyGuard when={dirty} includeSearch />
      <h4 className="font-medium">슬롯 단위 연결</h4>
      <p className="body-sm text-muted-foreground mt-1">
        객실 전체가 아니라 슬롯마다 따로 연결해야 하는 경우에만 사용합니다. 객실 전체 연결과 슬롯
        연결은 함께 쓸 수 없습니다.
      </p>
      {pendingTransitions.count > 0 && (
        <p role="alert" className="body-sm mt-2">
          결과 확인이 필요한 슬롯 요청이 {String(pendingTransitions.count)}건 있습니다.
          {pendingTransitions.items.length > 0 && (
            <>
              {' '}
              대상:{' '}
              {pendingTransitions.items
                .map((item) => `${item.roomName} · ${item.unitName}(${item.kind})`)
                .join(', ')}
              . 해당 슬롯을 열어 현재 상태를 조회하세요.
            </>
          )}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {room.units.map((item) => (
          <Button
            key={item.id}
            size="sm"
            variant={item.id === openUnitId ? 'solid' : 'outline'}
            aria-pressed={item.id === openUnitId}
            aria-label={`${item.name} 슬롯 연결 확인${pendingTransitions.items.some((held) => held.unitId === item.id) ? ' (요청 확인 필요)' : ''}`}
            onClick={() => {
              setOpenUnitId(item.id === openUnitId ? null : item.id);
            }}
          >
            {item.name}
            {pendingTransitions.items.some((held) => held.unitId === item.id) &&
              ' · 요청 확인 필요'}
          </Button>
        ))}
      </div>
      {unit && (
        <div className="mt-3">
          <EzremsUnitBindingSection
            key={unit.id}
            runtime={runtime}
            generation={generation}
            propertyId={propertyId}
            typeId={typeId}
            roomId={room.id}
            roomName={room.name}
            unit={{ id: unit.id, name: unit.name }}
            onBindingChanged={onBindingChanged}
            onDirtyChange={track('binding')}
          />
          <div className="mt-4">
            <EzremsUnitTransitionSection
              key={`transition:${unit.id}`}
              runtime={runtime}
              generation={generation}
              roomId={room.id}
              roomName={room.name}
              unit={{ id: unit.id, name: unit.name }}
              onApplied={onBindingChanged}
              onHeldChange={() => {
                setHeldTick((tick) => tick + 1);
              }}
              onDirtyChange={track('transition')}
            />
          </div>
          <div className="mt-4">
            <UnitPricingSection
              key={`pricing:${unit.id}`}
              runtime={runtime}
              generation={generation}
              roomName={room.name}
              transitionPending={transitions.items.some((held) => held.unitId === unit.id)}
              unit={{ id: unit.id, name: unit.name }}
              onChanged={onBindingChanged}
              onHeldChange={() => {
                setHeldTick((tick) => tick + 1);
              }}
              onDirtyChange={track('pricing')}
            />
          </div>
          <div className="mt-4">
            <UnitFirstDateSection
              key={`first-date:${unit.id}`}
              runtime={runtime}
              generation={generation}
              propertyId={propertyId}
              roomId={room.id}
              roomName={room.name}
              unit={{ id: unit.id, name: unit.name }}
              onChanged={onBindingChanged}
              onHeldChange={() => {
                setHeldTick((tick) => tick + 1);
              }}
              onDirtyChange={track('date')}
            />
          </div>
        </div>
      )}
    </section>
  );
}

/** 외부 연동 구획. 객실 단위 이지렘스 연결만 기존 조회·연결 기능을 그대로 쓴다. */
export function ExternalIntegrationZone({
  runtime,
  generation,
  propertyId,
  propertyName,
  type,
  room,
  onChooseSpace,
  onSelectRoom,
  onGoSales,
  selectionPending,
  selectionError,
  onRetrySelection,
  selectedUnitId,
  onSelectUnit,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  propertyName: string;
  type: NativeTypeDetails | undefined;
  room: NativeCatalogRoom | undefined;
  onChooseSpace: () => void;
  onSelectRoom: (roomId: string) => void;
  onGoSales: () => void;
  selectionPending: boolean;
  selectionError: unknown;
  onRetrySelection: () => void;
  selectedUnitId?: string | null;
  onSelectUnit?: (unitId: string | null) => void;
}) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const heading = useRef<HTMLHeadingElement>(null);
  const roomId = room?.id;
  useEffect(() => {
    if (!roomId) return;
    heading.current?.focus({ preventScroll: true });
    const scroll = heading.current && Reflect.get(heading.current, 'scrollIntoView');
    if (heading.current && typeof scroll === 'function')
      scroll.call(heading.current, { block: 'start' });
  }, [roomId]);
  const canWrite =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canWriteProperties(session.authorization);
  return (
    <div className="space-y-4">
      <section aria-label="객실 단위 이지렘스 연결" className="rounded-md border p-4">
        <h3 ref={heading} tabIndex={-1} className="scroll-mt-32 font-semibold">
          {room && type
            ? `${propertyName} · ${type.name} · ${room.name} 이지렘스 연결`
            : '객실 이지렘스 연결'}
        </h3>
        {selectionPending ? (
          <p role="status">선택한 객실을 불러오고 있습니다.</p>
        ) : selectionError ? (
          <div role="alert" className="space-y-2">
            <p>
              {isApiError(selectionError) && selectionError.status === 404
                ? '선택한 객실의 지점 소속을 확인할 수 없습니다. 객실을 다시 선택하세요.'
                : '선택한 객실 정보를 불러오지 못했습니다.'}
            </p>
            <Button variant="outline" onClick={onRetrySelection}>
              객실 다시 조회
            </Button>
          </div>
        ) : room && type ? (
          canWrite ? (
            <div className="mt-3">
              <EzremsRoomBindingSection
                key={room.id}
                runtime={runtime}
                generation={generation}
                propertyId={propertyId}
                typeId={type.id}
                typeName={type.name}
                room={room}
                onGoSales={onGoSales}
                onBindingChanged={() => {
                  void queryClient.invalidateQueries({
                    queryKey: [
                      'native-provider-state',
                      runtime.apiNamespace,
                      generation,
                      propertyId,
                    ],
                  });
                }}
              />
              <UnitBindingEntry
                key={`units:${room.id}`}
                runtime={runtime}
                generation={generation}
                propertyId={propertyId}
                typeId={type.id}
                room={room}
                selectedUnitId={selectedUnitId}
                onSelectUnit={onSelectUnit}
                onBindingChanged={() => {
                  void queryClient.invalidateQueries({
                    queryKey: [
                      'native-provider-state',
                      runtime.apiNamespace,
                      generation,
                      propertyId,
                    ],
                  });
                }}
              />
            </div>
          ) : (
            <p className="body-sm text-muted-foreground mt-2" role="status">
              객실 연결 확인에는 지점 수정 권한이 필요합니다. 현재 권한으로는 사용할 수 없습니다.
            </p>
          )
        ) : (
          <>
            <p className="body-sm text-muted-foreground mt-2">
              연결은 객실마다 확인합니다. 객실을 먼저 선택하세요.
            </p>
            <Button className="mt-3" variant="outline" onClick={onChooseSpace}>
              객실 선택
            </Button>
          </>
        )}
      </section>
      <NativeProviderStateSection
        runtime={runtime}
        generation={generation}
        propertyId={propertyId}
        onRoom={onSelectRoom}
        currentRoomId={room?.id}
      />
    </div>
  );
}

/** 출입 탭 머리말. 조회되는 정보, 조회하지 않는 정보, 실제 장비에서 확인되지 않은 정보를 나눠 적는다. */
export function AccessCoverage() {
  const rows: [string, string, string][] = [
    ['객실 출입 설정', '조회·수정 가능', '객실을 선택하면 저장된 온라인 출입 설정을 봅니다.'],
    ['장비와 연결된 슬롯', '조회 가능', '아래 장비 목록에서 연결된 슬롯을 확인합니다.'],
    [
      '공동 현관·객실 문 구분, 출입 그룹',
      '조회 미지원',
      '이 화면에서 아직 조회하지 않습니다. 없다는 뜻이 아닙니다.',
    ],
    [
      '현재 출입 가능한 사용자',
      '조회 미지원',
      '장비를 기준으로 사용자를 찾는 기능은 아직 없습니다.',
    ],
    [
      '실제 장비에서의 적용 여부',
      '확인되지 않음',
      '저장된 설정은 장비 적용이나 실제 출입 권한 부여를 뜻하지 않습니다.',
    ],
  ];
  return (
    <section aria-label="출입 정보 확인 범위" className="rounded-md border p-4">
      <h2 className="font-semibold">출입 정보 확인 범위</h2>
      <dl className="body-sm mt-3 grid gap-3 sm:grid-cols-2">
        {rows.map(([name, status, note]) => (
          <div key={name}>
            <dt className="font-medium">
              {name} · <span className="text-muted-foreground">{status}</span>
            </dt>
            <dd className="text-muted-foreground">{note}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const REASON_LABEL: Record<NonNullable<NativeProviderRoom['unavailable_reason']>, string> = {
  ROOM_NOT_CORE_OWNED: '이 객실은 Core에서 직접 관리하는 객실이 아닙니다',
  ROOM_TYPE_UNAVAILABLE: '객실 유형을 확인할 수 없습니다',
  UNIT_SCOPE_UNAVAILABLE: '객실의 슬롯 범위를 확인할 수 없습니다',
  BINDING_EVIDENCE_INVALID: '저장된 연결 기록을 검증하지 못했습니다',
  NAMESPACE_UNAVAILABLE: '연결할 환경 정보를 확인할 수 없습니다',
  SCOPE_CHANGED: '조회하는 사이 객실 구성이 바뀌었습니다',
};
const OPERATION_STATE: Record<NativeProviderOperation['terminal_state'], string> = {
  PENDING: '결과 확인 중',
  UNKNOWN: '결과 확인 필요',
  APPLIED: '기록된 결과: 적용됨',
  NOT_APPLIED: '기록된 결과: 미적용',
};

const PROOF_LABEL: Record<NonNullable<NativeProviderOperation['proof_kind']>, string> = {
  REMOTE_FINALITY: '외부 최종 결과',
  SYNC_RESPONSE_AND_READBACK: '즉시 응답과 재조회',
  FINAL_NON_APPLICATION: '최종 미적용 확인',
};
const STATUS_REASON: Record<NonNullable<EzremsTransitionStatus['reason']>, string> = {
  AWAITING_RESULT: '결과 대기',
  TRANSPORT_UNKNOWN: '전송 여부 미확인',
  READBACK_UNAVAILABLE: '현재 상태 조회 불가',
  READBACK_MISMATCH: '현재 상태 불일치',
  FINALITY_UNCONFIRMED: '최종 반영 미확인',
};
const BOOKING_SALES = 32;

/** 기록 결과는 Booking 판매 권한(32)으로 기존 판매 요청 조회를 그대로 쓴다. 지점 권한과 별개다. */
function OperationResult({
  runtime,
  roomId,
  operation,
  label,
}: {
  runtime: PropertyRuntime;
  roomId: string;
  operation: NativeProviderOperation;
  label: string;
}) {
  const [status, setStatus] = useState<EzremsTransitionStatus | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    setStatus(null);
    setMessage('');
    const generation = runtime.session.getSnapshot().generation;
    try {
      const before = await actor(runtime, BOOKING_SALES, 'booking');
      const result = await readEzremsTransitionCommand(
        runtime.client,
        roomId,
        operation.operation_id,
        operation.target_kind,
      );
      const after = await actor(runtime, BOOKING_SALES, 'booking');
      if (before !== after || runtime.session.getSnapshot().generation !== generation)
        throw new Error('작업자가 변경되어 결과를 표시하지 않습니다.');
      setStatus(result);
    } catch (error) {
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('booking', generation);
      setMessage(
        isApiError(error)
          ? error.status === 403 || error.status === 401
            ? '판매 권한을 다시 확인해 주세요.'
            : error.status === 404
              ? '이 기록을 찾을 수 없습니다. 결과를 단정하지 않습니다.'
              : '기록 결과를 확인하지 못했습니다.'
          : error instanceof Error
            ? error.message
            : '기록 결과를 확인하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1 space-y-1">
      <Button variant="outline" disabled={busy} onClick={() => void check()}>
        {label} 결과 다시 확인
      </Button>
      {status && (
        <p role="status">
          현재 결과: {OPERATION_STATE[status.outcome]} · 전송{' '}
          {status.transport === 'DELIVERED' ? '전달 확인' : '미확인'} · 대상 상태 비교{' '}
          {status.readback_matches === null
            ? '미확인'
            : status.readback_matches
              ? '일치'
              : '불일치'}
          {status.reason ? ` · ${STATUS_REASON[status.reason]}` : ''}
          {status.pending ? ' · 처리 중' : ''}
        </p>
      )}
      {message && <p role="alert">{message}</p>}
    </div>
  );
}

function providerStateError(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '이 지점의 연동 상태를 볼 권한이 없습니다.';
    // 404는 삭제나 미연결로 단정하지 않는다. 조회 기능이 아직 제공되지 않은 환경일 수 있다.
    if (error.status === 404)
      return '이 지점의 연결 상태를 확인하지 못해 연결 여부를 알 수 없습니다.';
    if (error.status === 422)
      return '요청한 페이지를 읽을 수 없습니다. 처음부터 다시 조회해 주세요.';
    if (error.status === 503)
      return '연동 상태를 지금 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    if (error.kind === 'invalid-response')
      return '연결 정보를 불러오지 못했습니다. 다시 조회해 주세요.';
  }
  return '연동 상태를 불러오지 못했습니다.';
}

function RoomProviderRow({
  runtime,
  room,
  canReadBooking,
  onRoom,
  current,
}: {
  runtime: PropertyRuntime;
  room: NativeProviderRoom;
  canReadBooking: boolean;
  onRoom: (id: string) => void;
  current: boolean;
}) {
  const hidden = room.binding_visibility === 'REQUIRES_PROPERTY_WRITE';
  return (
    <li className="space-y-1 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-medium">{room.room_name}</h4>
        <span>
          {room.binding_status === 'BOUND'
            ? '연결됨'
            : room.binding_status === 'UNBOUND'
              ? '연결 설정 없음(확인됨)'
              : '판정할 수 없음'}
        </span>
      </div>
      {room.unavailable_reason && (
        <p className="body-sm text-muted-foreground">{REASON_LABEL[room.unavailable_reason]}</p>
      )}
      {hidden ? (
        <p className="body-sm text-muted-foreground">
          연결 위치는 지점 수정 권한이 있을 때만 보입니다. 연결 여부와는 별개입니다.
        </p>
      ) : (
        room.binding && (
          <p className="body-sm text-muted-foreground">
            지점 {room.binding.branch_code} · 프로젝트 {room.binding.project_code} ·{' '}
            {room.binding.dong}동 {room.binding.floor}층 {room.binding.ho2}호
          </p>
        )
      )}
      {room.operations.length > 0 && (
        <ul aria-label={`${room.room_name} 최근 변경 기록`} className="body-sm space-y-1">
          {room.operations.map((operation, index) => (
            <li key={operation.operation_id}>
              {operation.action === 'room.sales.transition' ? '판매 유형 전환' : '첫 입주일 변경'} ·{' '}
              {operation.target_kind === 'MIDTERM' ? '중기' : '일반'} ·{' '}
              {OPERATION_STATE[operation.terminal_state]}
              {operation.transport === 'UNKNOWN' ? ' · 전달 여부 확인 불가' : ''}
              {operation.transport === 'DELIVERED' ? ' · 전달됨' : ''}
              {operation.readback_matches === null
                ? ''
                : ` · 대상 상태 비교 ${operation.readback_matches ? '일치' : '불일치'}`}
              {operation.proof_kind
                ? ` · 확인 근거 ${PROOF_LABEL[operation.proof_kind]}${operation.proof_observed_at ? ` (${formatDateTimeWithOffset(operation.proof_observed_at)})` : ''}`
                : ''}{' '}
              · 기록 {formatDateTimeWithOffset(operation.recorded_at)}
              {canReadBooking && (
                <OperationResult
                  runtime={runtime}
                  roomId={room.room_id}
                  operation={operation}
                  label={`${room.room_name} 기록 ${String(index + 1)}`}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {room.operations.length > 0 && !canReadBooking && (
        <p className="body-sm text-muted-foreground">
          기록별 현재 결과 확인은 판매 권한이 있을 때만 할 수 있습니다.
        </p>
      )}
      {current ? (
        <p className="body-sm text-muted-foreground">위에서 확인 중인 객실입니다.</p>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            onRoom(room.room_id);
          }}
        >
          {room.room_name} 연결 자세히 보기
        </Button>
      )}
    </li>
  );
}

/** 지점 전체 객실의 이지렘스 연결·변경 기록. Core에 저장된 기록만 읽고 실제 장비 상태는 조회하지 않는다. */
function NativeProviderStateSection({
  runtime,
  generation,
  propertyId,
  onRoom,
  currentRoomId,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  onRoom: (roomId: string) => void;
  currentRoomId?: string;
}) {
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1) ?? null;
  const session = useOperatorSession(runtime.session);
  const canReadBooking =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('booking') &&
    !!session.authorization &&
    canReadDomain(session.authorization, 'booking', BOOKING_SALES);
  const query = useQuery({
    queryKey: ['native-provider-state', runtime.apiNamespace, generation, propertyId, cursor],
    queryFn: ({ signal }) =>
      readNativeProviderState(runtime.client, propertyId, { cursor, signal }),
    retry: false,
    gcTime: 0,
  });
  const data = query.data;
  // 손상·다른 지점 커서(422)는 같은 커서로 다시 보내지 않고 첫 페이지부터 다시 읽는다.
  const restart = isApiError(query.error) && query.error.status === 422;
  return (
    <section aria-label="지점 전체 외부 연동" className="rounded-md border p-4">
      <h3 className="font-semibold">지점 전체 연동 상태</h3>
      {query.isPending && <p role="status">연동 상태를 불러오고 있습니다.</p>}
      {query.isError && (
        <>
          <p role="alert" className="body-sm mt-2">
            {providerStateError(query.error)}
          </p>
          <Button
            className="mt-2"
            variant="outline"
            disabled={query.isFetching}
            onClick={() => {
              if (restart && cursors.length) setCursors([]);
              else void query.refetch();
            }}
          >
            {restart && cursors.length ? '처음 객실부터 다시 조회' : '다시 조회'}
          </Button>
        </>
      )}
      {data && !query.isError && (
        <div className="mt-2 space-y-3">
          <p className="body-sm" role="status">
            이번 페이지 객실 {data.page_summary.rooms}개 · 연결됨 {data.page_summary.bound} · 연결
            설정 없음 {data.page_summary.unbound} · 판정할 수 없음 {data.page_summary.unavailable}
          </p>
          <p className="body-sm text-muted-foreground">
            연결 상태 확인 시각: {formatDateTimeWithOffset(data.observed_at)} · 현재 페이지
            기준입니다. 외부 서비스의 실제 반영 여부는 미확인입니다.
          </p>
          {data.rooms.length ? (
            <ul aria-label="객실별 연동 상태" className="space-y-2">
              {data.rooms.map((item) => (
                <RoomProviderRow
                  key={`${String(generation)}:${item.room_id}`}
                  runtime={runtime}
                  room={item}
                  canReadBooking={canReadBooking}
                  onRoom={onRoom}
                  current={item.room_id === currentRoomId}
                />
              ))}
            </ul>
          ) : (
            <p>이 페이지에는 객실이 없습니다.</p>
          )}
          <div className="flex flex-wrap gap-2">
            {cursors.length > 0 && (
              <>
                <Button
                  variant="outline"
                  disabled={query.isFetching}
                  onClick={() => {
                    setCursors(cursors.slice(0, -1));
                  }}
                >
                  이전 객실
                </Button>
                <Button
                  variant="outline"
                  disabled={query.isFetching}
                  onClick={() => {
                    setCursors([]);
                  }}
                >
                  처음 객실부터
                </Button>
              </>
            )}
            {data.page.next_cursor && (
              <Button
                variant="outline"
                disabled={query.isFetching}
                onClick={() => {
                  setCursors([...cursors, String(data.page.next_cursor)]);
                }}
              >
                다음 객실
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
